import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { outfitService, Outfit } from '../services/outfit';
import { friendService, Friend } from '../services/friend';
import { Button } from '../components/common/Button';
import { getImageUrl } from '../utils/imageUrl';

export const ShareOutfit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [outfit, setOutfit] = useState<Outfit | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadData = async () => {
    try {
      const [outfitData, friendsData] = await Promise.all([
        outfitService.getById(id!),
        friendService.getFriends(),
      ]);
      setOutfit(outfitData);
      setFriends(friendsData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load outfit or friends');
    } finally {
      setLoading(false);
    }
  };

  const toggleFriend = (friendId: string) => {
    setSelectedFriends((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleShare = async () => {
    if (selectedFriends.length === 0) {
      setError('Please select at least one friend');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await outfitService.share(id!, {
        recipient_ids: selectedFriends,
      });
      navigate('/outfits');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to share outfit');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!outfit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Outfit not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Share Outfit</h1>

          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">{outfit.name}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {outfit.items.map((item) => (
                <div key={item.id} className="aspect-square rounded-xl overflow-hidden">
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.category}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Friends ({selectedFriends.length} selected)
            </label>
            {friends.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                You don't have any friends yet. Add friends first!
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {friends.map((friendship) => {
                  const isSelected = selectedFriends.includes(friendship.friend_id);
                  return (
                    <div
                      key={friendship.id}
                      onClick={() => toggleFriend(friendship.friend_id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-yellow-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-xl font-bold">
                          {friendship.friend?.username?.[0]?.toUpperCase() || 'F'}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{friendship.friend?.username || 'Unknown'}</p>
                        </div>
                        {isSelected && (
                          <span className="text-primary text-xl">✓</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => navigate('/outfits')}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleShare}
              disabled={submitting || selectedFriends.length === 0}
            >
              {submitting ? 'Sharing...' : 'Share Outfit'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

